import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingBag, Plus, Trash2, Save, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost, fmt } from '../lib/api';
import { API_BASE } from '../lib/api';

const ORDER_STATUSES = ['awaiting_payment', 'confirmed', 'picked', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLOR = {
  awaiting_payment: 'text-warn bg-warn/10',
  confirmed: 'text-ok bg-ok/10',
  picked: 'text-amber2 bg-amber2/10',
  shipped: 'text-moss bg-moss/10',
  delivered: 'text-ok bg-ok/10',
  cancelled: 'text-bad bg-bad/10',
};

async function apiPatch(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
}

async function apiDelete(path) {
  const res = await fetch(`${API_BASE}${path}`, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) throw new Error('Delete failed');
  return res.json();
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-emerald-950/10 rounded-2xl p-5">
      <p className="font-mono text-[10px] uppercase tracking-widest text-sub">{label}</p>
      <p className="font-display font-bold text-3xl text-forest mt-1">{value}</p>
      {sub && <p className="text-xs text-sub mt-1">{sub}</p>}
    </div>
  );
}

function OrdersTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [filter, setFilter] = useState('');
  const load = () => apiGet('/admin/orders', filter ? { status: filter } : {}).then(setData).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (num, status) => {
    try {
      await apiPatch(`/admin/orders/${num}`, { status });
      toast.success(`Order ${num} → ${status}`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button data-testid="admin-order-filter-all" onClick={() => setFilter('')} className={`font-mono text-[11px] uppercase px-3 py-1.5 rounded-full border transition ${!filter ? 'bg-forest text-cream border-forest' : 'border-emerald-950/15'}`}>All</button>
        {ORDER_STATUSES.map((s) => (
          <button key={s} data-testid={`admin-order-filter-${s}`} onClick={() => setFilter(s)} className={`font-mono text-[11px] uppercase px-3 py-1.5 rounded-full border transition ${filter === s ? 'bg-forest text-cream border-forest' : 'border-emerald-950/15'}`}>{s.replace(/_/g, ' ')}</button>
        ))}
      </div>
      <div className="space-y-3">
        {data.items.map((o) => (
          <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="bg-white border border-emerald-950/10 rounded-2xl p-5 flex flex-wrap items-center gap-4">
            <div className="min-w-[180px]">
              <p className="font-mono text-sm font-semibold text-forest">{o.order_number}</p>
              <p className="text-xs text-sub mt-0.5">{new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {o.customer.name}</p>
              <p className="text-xs text-sub">{o.items.length} item{o.items.length !== 1 ? 's' : ''} · {o.customer.city}, {o.customer.state}</p>
            </div>
            <span className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${STATUS_COLOR[o.status] || 'text-sub bg-silver'}`}>{o.status.replace(/_/g, ' ')}</span>
            <span className="font-display font-bold text-xl text-forest ml-auto">{fmt(o.total)}</span>
            <select
              data-testid={`admin-order-status-${o.order_number}`}
              value={o.status}
              onChange={(e) => setStatus(o.order_number, e.target.value)}
              className="text-sm border border-emerald-950/15 rounded-full px-3 py-2 bg-cream focus:outline-none focus:border-flame"
            >
              {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        ))}
        {data.items.length === 0 && <p className="text-sub text-sm py-10 text-center">No orders match this filter.</p>}
      </div>
    </div>
  );
}

function ProductsTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState({ name: '', category_slug: 'engine-parts', price: '', stock: '10', sku: '', oem_number: '', brands: [], description: '', featured: false });

  const load = () => apiGet('/admin/products', search ? { search } : {}).then(setData).catch(() => {});
  useEffect(() => { load(); }, [search]);
  useEffect(() => {
    apiGet('/categories').then(setCategories).catch(() => {});
    apiGet('/brands').then(setBrands).catch(() => {});
  }, []);

  const edit = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...e[id], [k]: v } }));

  const save = async (p) => {
    const e = edits[p.id] || {};
    try {
      await apiPatch(`/admin/products/${p.id}`, {
        price: e.price !== undefined ? Number(e.price) : undefined,
        stock: e.stock !== undefined ? Number(e.stock) : undefined,
        featured: e.featured !== undefined ? e.featured : undefined,
      });
      toast.success(`${p.name} updated`);
      setEdits((x) => ({ ...x, [p.id]: {} }));
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (p) => {
    try {
      await apiDelete(`/admin/products/${p.id}`);
      toast.success(`${p.name} deleted`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const create = async (ev) => {
    ev.preventDefault();
    try {
      await apiPost('/admin/products', { ...form, price: Number(form.price), stock: Number(form.stock), badge: null });
      toast.success('Product added');
      setShowAdd(false);
      setForm({ name: '', category_slug: 'engine-parts', price: '', stock: '10', sku: '', oem_number: '', brands: [], description: '', featured: false });
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const input = 'bg-cream border border-emerald-950/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-flame';

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sub" />
          <input data-testid="admin-product-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or SKU…" className={`${input} w-full pl-9`} />
        </div>
        <button data-testid="admin-add-product-toggle" onClick={() => setShowAdd(!showAdd)} className="inline-flex items-center gap-2 bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide text-sm px-5 py-2.5 rounded-full transition">
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {showAdd && (
        <form onSubmit={create} data-testid="admin-add-product-form" className="bg-white border border-emerald-950/10 rounded-2xl p-6 mb-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <input required className={input} placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-new-name" />
          <select className={input} value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })} data-testid="admin-new-category">
            {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
          </select>
          <input required className={input} placeholder="SKU (e.g. EN-7001)" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} data-testid="admin-new-sku" />
          <input className={input} placeholder="OEM number" value={form.oem_number} onChange={(e) => setForm({ ...form, oem_number: e.target.value })} data-testid="admin-new-oem" />
          <input required type="number" step="0.01" min="0.01" className={input} placeholder="Price USD" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-new-price" />
          <input required type="number" min="0" className={input} placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="admin-new-stock" />
          <div className="lg:col-span-2">
            <select className={`${input} w-full`} value={form.brands[0] || ''} onChange={(e) => setForm({ ...form, brands: e.target.value ? [e.target.value] : [] })} data-testid="admin-new-brand">
              <option value="">Universal fit (no brand)</option>
              {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </div>
          <textarea className={`${input} sm:col-span-2 lg:col-span-3`} placeholder="Short description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-new-description" />
          <button className="bg-forest hover:bg-flame text-cream font-display font-semibold uppercase tracking-wide rounded-full transition" data-testid="admin-new-submit">Save Product</button>
        </form>
      )}

      <div className="space-y-3">
        {data.items.map((p) => {
          const e = edits[p.id] || {};
          const dirty = Object.keys(e).length > 0;
          return (
            <div key={p.id} data-testid={`admin-product-${p.sku}`} className="bg-white border border-emerald-950/10 rounded-2xl p-4 flex flex-wrap items-center gap-3">
              <img src={p.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-forest shrink-0" />
              <div className="min-w-[200px] flex-1">
                <p className="font-semibold text-sm text-forest leading-tight">{p.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-sub mt-0.5">{p.sku} · {p.category_name}</p>
              </div>
              <label className="text-xs text-sub flex items-center gap-2">
                $
                <input
                  data-testid={`admin-price-${p.sku}`}
                  type="number" step="0.01" min="0.01"
                  value={e.price !== undefined ? e.price : p.price}
                  onChange={(ev) => edit(p.id, 'price', ev.target.value)}
                  className={`${input} w-24`}
                />
              </label>
              <label className="text-xs text-sub flex items-center gap-2">
                Stock
                <input
                  data-testid={`admin-stock-${p.sku}`}
                  type="number" min="0"
                  value={e.stock !== undefined ? e.stock : p.stock}
                  onChange={(ev) => edit(p.id, 'stock', ev.target.value)}
                  className={`${input} w-20`}
                />
              </label>
              <label className="flex items-center gap-1.5 text-xs text-sub cursor-pointer">
                <input type="checkbox" checked={e.featured !== undefined ? e.featured : p.featured} onChange={(ev) => edit(p.id, 'featured', ev.target.checked)} className="w-4 h-4 accent-[#FF5722]" data-testid={`admin-featured-${p.sku}`} />
                Featured
              </label>
              <div className="flex gap-2 ml-auto">
                <button data-testid={`admin-save-${p.sku}`} onClick={() => save(p)} disabled={!dirty} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide bg-forest disabled:opacity-30 hover:bg-flame text-cream px-4 py-2 rounded-full transition">
                  <Save className="w-3.5 h-3.5" /> Save
                </button>
                <button data-testid={`admin-delete-${p.sku}`} onClick={() => remove(p)} className="p-2 text-sub hover:text-bad transition" aria-label="Delete product"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-sub mt-4">{data.total} products total</p>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav('/login?next=/admin');
  }, [user, loading, nav]);

  useEffect(() => {
    if (user?.role === 'admin') apiGet('/admin/stats').then(setStats).catch(() => {});
  }, [user, tab]);

  if (loading || !user) {
    return <div className="bg-cream min-h-screen pt-40"><div className="max-w-3xl mx-auto px-4 animate-pulse space-y-4"><div className="h-10 bg-silver rounded w-1/3" /><div className="h-40 bg-silver rounded-2xl" /></div></div>;
  }

  if (user.role !== 'admin') {
    return (
      <div className="bg-cream min-h-screen grid place-items-center px-4 py-32 text-center" data-testid="admin-denied">
        <div>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-forest text-4xl">Admins only</h1>
          <p className="text-sub mt-3 mb-8">This account doesn't have admin access.</p>
          <Link to="/" className="inline-block bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors">Back to Store</Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'products', label: 'Products', icon: Package },
  ];

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-forest noise relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-2">Admin panel</p>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl">Parts HQ</h1>
          </div>
          <div className="flex gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 font-display font-semibold uppercase tracking-wide text-sm px-5 py-2.5 rounded-full transition ${tab === t.id ? 'bg-flame text-white' : 'bg-white/5 text-cream/70 hover:text-cream border border-white/10'}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {tab === 'dashboard' && stats && (
          <div data-testid="admin-dashboard" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Paid revenue" value={fmt(stats.revenue)} sub="confirmed → delivered" />
            <StatCard label="Orders" value={stats.orders} sub={`${stats.awaiting_payment} awaiting payment`} />
            <StatCard label="Products" value={stats.products} sub={`${stats.low_stock} low stock (<10)`} />
            <StatCard label="Customers" value={stats.customers} sub={`${stats.messages} contact messages`} />
          </div>
        )}
        {tab === 'orders' && <OrdersTab />}
        {tab === 'products' && <ProductsTab />}
      </div>
    </div>
  );
}
